import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { UnifiedTask } from './TaskCard';
import DueDateInput, { isValidCalendarDate } from './DueDateInput';

interface DueDateModalProps {
    show: boolean;
    onHide: () => void;
    task: UnifiedTask;
    onConfirm: (dueDate: string | null) => Promise<void>;
    /** Pre-fills this date instead of the task's own (a calendar drop); the current one is shown as "Nu". */
    initialDate?: string;
}

const DueDateModal: React.FC<DueDateModalProps> = ({ show, onHide, task, onConfirm, initialDate }) => {
    const [dueDate, setDueDate] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const currentDate = task.dueDate ? task.dueDate.split(' ')[0] : '';

    // Reset form to the proposed or current due date whenever the modal opens
    useEffect(() => {
        if (show) {
            setDueDate(initialDate ?? currentDate);
            setError(null);
        }
    }, [show, currentDate, initialDate]);

    const save = async (value: string | null) => {
        try {
            setSubmitting(true);
            setError(null);
            await onConfirm(value);
            onHide();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Kunde inte uppdatera slutdatum';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (dueDate && !isValidCalendarDate(dueDate)) {
            setError('Ogiltigt datum, använd formatet ÅÅÅÅ-MM-DD');
            return;
        }
        save(dueDate || null);
    };

    const handleClear = () => {
        setDueDate('');
        save(null);
    };

    const handleClose = () => {
        if (!submitting) {
            setError(null);
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered dialogClassName="due-date-modal">
            <Modal.Header closeButton>
                <Modal.Title>Förfallodatum</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                            {error}
                        </Alert>
                    )}
                    <p className="text-muted mb-3">{task.title}</p>
                    {initialDate !== undefined && (
                        <p className="mb-3">Nu: {currentDate || 'inget förfallodatum'}</p>
                    )}
                    <Form.Group>
                        <Form.Label>Förfallodatum</Form.Label>
                        <DueDateInput value={dueDate} onChange={setDueDate} disabled={submitting} autoFocus />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    {task.dueDate && (
                        <Button
                            variant="outline-danger"
                            onClick={handleClear}
                            disabled={submitting}
                            className="me-auto"
                        >
                            Ta bort slutdatum
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? 'Sparar...' : 'Spara'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DueDateModal;
